import { Distance, EventEmitter, Point } from '../../../../../FrontendBundle/Resources/js/ofcs';
import AddItemCommand from '../command/addItemCommand';
import PasteItemCommand from '../command/pasteItemCommand';
import InsertItem from '../item/insertItem';
import Cell from './cell';
import Composite from './composite';

const { GridItem } = Ofcs.Editor.Item;

/**
 * @class GridManager
 * @extends EventEmitter
 */
export default class GridManager extends EventEmitter {
    /**
     * Initializes the guide manager
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool Select tool
     * @param {ContextManager} contextManager Context Manager
     * @param {CommandExecutor} commandExecutor Command Executor
     */
    constructor(selectTool, contextManager, commandExecutor) {
        super();
        this.contextManager = contextManager;
        this.commandExecutor = commandExecutor;

        selectTool.on('startmove', this.start, this);
        selectTool.on('move', this.check, this);
        selectTool.on('finishmove', this.stop, this);

        commandExecutor.on('beforeExecute', (command) => {
            if (!(command instanceof AddItemCommand) && !(command instanceof PasteItemCommand)) {
                return;
            }

            const notFittingItem = command.items
                .find((item) => {
                    const parent = item.getParentItem();

                    if (!(parent instanceof GridItem && item instanceof InsertItem)) return false;

                    const box = parent.getParentItem();

                    const depthDiff = box.get('depth') - item.get('depth');

                    // Allow a difference of up tp 3 mm
                    if (depthDiff < 0 || depthDiff > 3) return true;

                    return !item.getParentItem().getFreeCell(item);
                });

            if (notFittingItem) command.abort();
        });
    }

    /**
     * Start guide check, store selection bounds
     * If nothing is selected, do nothing
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {Ofcs.Editor.Command.MoveItemsCommand} command
     * @param {Event} event Event
     */
    start(selectTool, selection, command, event) {
        const grid = this.contextManager.getCurrentItem();

        delete this.startBounds;
        delete this.startMouseCell;

        if (!selection.length) return;
        if (!(grid instanceof GridItem)) return;

        this.startBounds = selection.getBounds();
        this.startMouseCell = this.getCellByAbsolutePoint(grid, event.point);

        this.trigger('start', this);
    }

    /**
     * Stop guides check
     */
    stop() {
        delete this.startBounds;
        delete this.startMouseCell;

        this.trigger('stop', this);
    }

    /**
     * Check for guides and snap
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {Distance} distance
     * @param {Ofcs.Editor.Command.MoveItemsCommand} command
     * @param {Event} event
     */
    check(selectTool, selection, distance, command, event) {
        const grid = this.contextManager.getCurrentItem();
        if (!(grid instanceof GridItem)) return;

        const item = selection.first();

        const itemArea = new Composite(grid.itemToCell(item), grid.getItemDimensions(item));

        const cellCenterOffset = new Distance(grid.getCellWidth() / 2, grid.getCellHeight() / 2);
        const startCellCenter = this.startBounds.getTopLeft().clone().move(cellCenterOffset);
        const startCell = this.getCellByRelativePoint(grid, startCellCenter);
        const mouseCell = this.getCellByAbsolutePoint(grid, event.point);
        const cellDistance = this.startMouseCell.getDistance(mouseCell);
        const targetCell = startCell.move(cellDistance);
        const targetArea = itemArea.moveToCell(targetCell);

        let targetPosition;

        if (!this.startBounds) return;

        if (grid.isCompositeInsideGrid(targetArea) && grid.isAreaFree(targetArea, item)) {
            targetPosition = new Point(
                targetCell.getColumn() * grid.getCellWidth(),
                targetCell.getRow() * grid.getCellHeight(),
            );
        } else {
            // TODO: We should instead find the closest matching free area in the grid
            targetPosition = item.getBounds().getTopLeft();
        }

        const { point } = distance;
        point.x = targetPosition.x - this.startBounds.getTopLeft().x;
        point.y = targetPosition.y - this.startBounds.getTopLeft().y;
    }

    /**
     *
     * @param {GridItem} grid
     * @param {Point} point
     *
     * @return {Cell}
     */
    getCellByRelativePoint(grid, { x, y }) {
        const column = Math.floor(x / grid.getCellWidth());
        const row = Math.floor(y / grid.getCellHeight());

        return new Cell(Math.max(row, 0), Math.max(column, 0));
    }

    /**
     *
     * @param {GridItem} grid
     * @param {Point} point
     *
     * @return {Cell}
     */
    getCellByAbsolutePoint(grid, { x, y }) {
        const column = Math.floor((x - grid.getAbsolutePosition().x) / grid.getCellWidth());
        const row = Math.floor((y - grid.getAbsolutePosition().y) / grid.getCellHeight());

        return new Cell(Math.max(row, 0), Math.max(column, 0));
    }
}
